<script setup lang="ts">
import { computed, ref } from "vue";
import {
    UiButton,
    UiCheckbox,
    UiChip,
    UiDatePicker,
    UiFileUpload,
    UiInputField,
    UiMultiSelectChips,
    UiRichTextEditor,
    UiSelect,
    UiSingleFileUpload,
    UiTextarea,
    UiText,
    useApi,
} from "@shopexperts/shared";
import { useQuery } from "@tanstack/vue-query";
import FormGroupPanel from "./FormGroupPanel.vue";
import MetricsDialog from "./MetricsDialog.vue";
import MetricsRow from "./MetricsRow.vue";
import UiSortableImageList from "./UiSortableImageList.vue";
import { useMediaUploadIntegration } from "@/pages/expert/listing/composables/useMediaUploadIntegration";
import { useCustomerStoryIndustries } from "@/app/composables/customer-stories/useCustomerStoryIndustries";
import { useExpertBadgeSearch } from "@/app/composables/badges/useExpertBadgeSearch";
import {
    SHOPIFY_PLAN_OPTIONS,
    TESTIMONIAL_ROLE_OPTIONS,
    type CustomerStoryFormModel,
} from "@/app/composables/customer-stories/customerStoryFormModel";

interface CustomerStoryOptionGroup {
    id: string;
    label: string;
    items: Array<{
        label: string;
        value: string;
    }>;
}

const props = defineProps<{
    mode: "create" | "edit";
    modelValue: CustomerStoryFormModel;
    busy?: boolean;
}>();

const emit = defineEmits<{
    (e: "update:modelValue", value: CustomerStoryFormModel): void;
    (e: "save", status: "draft" | "published"): void;
    (e: "cancel"): void;
}>();

const model = computed({
    get: () => props.modelValue,
    set: (value: CustomerStoryFormModel) => emit("update:modelValue", value),
});

const metricsDialogOpen = ref(false);
const { uploadFn, deleteFn } = useMediaUploadIntegration();
const { badges } = useExpertBadgeSearch();
const { data: industries } = useCustomerStoryIndustries();
const api = useApi();

const categoriesQuery = useQuery({
    queryKey: ["service-categories", "with-subs"],
    queryFn: () => api.categories.getCategoriesWithSubcategories(),
    staleTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
});

const storyStatusChip = computed(() => {
    if (model.value.status === "published") {
        return { text: "Published", variant: "success" as const };
    }
    return { text: "Draft", variant: "neutral" as const };
});

const industryOptions = computed(() =>
    (industries.value || []).map((industry) => ({
        label: industry.label,
        value: industry.slug,
    })),
);

const subcategoryOptions = computed(() =>
    (categoriesQuery.data.value || []).flatMap((category) =>
        category.subcategories.map((subcategory) => ({
            label: `${category.name}: ${subcategory.name}`,
            value: String(subcategory.id),
        })),
    ),
);

const subcategoryOptionGroups = computed<CustomerStoryOptionGroup[]>(() =>
    (categoriesQuery.data.value || []).map((category) => ({
        id: `category-${category.id}`,
        label: category.name,
        items: category.subcategories.map((subcategory) => ({
            label: subcategory.name,
            value: String(subcategory.id),
        })),
    })),
);

const badgeOptions = computed(() =>
    (badges.value || []).map((badge) => ({
        label: badge.name,
        value: String(badge.id),
    })),
);

const titleError = computed(() => !model.value.title.trim() ? "Title is required" : "");

function save(status: "draft" | "published") {
    if (!model.value.title.trim()) return;
    emit("save", status);
}

async function removeGalleryItem(item: CustomerStoryFormModel["gallery"][number]) {
    model.value = {
        ...model.value,
        gallery: model.value.gallery.filter((galleryItem) => galleryItem !== item),
    };

    if (!item.media_id || !deleteFn) return;
    await deleteFn(item);
}
</script>

<template>
    <div class="mx-auto flex w-full max-w-[896px] flex-col gap-4">
        <div class="flex flex-col-reverse gap-4 md:flex-row md:items-center md:justify-between md:gap-10">
            <div class="flex min-w-0 items-center gap-3">
                <button
                    class="group inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-se-gray-300 bg-white text-se-gray-700 transition-all hover:border-se-gray-400 hover:bg-se-gray-50 hover:text-se-gray-900"
                    type="button"
                    aria-label="Back to case studies"
                    @click="emit('cancel')"
                >
                    <svg
                        class="h-4 w-4 transition-transform group-hover:-translate-x-0.5"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.5"
                    >
                        <path d="M10 4l-4 4 4 4" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </button>
                <UiText as="h2" variant="h5" :truncate="1" class="min-w-0 flex-1 font-semibold text-se-dark">
                    {{ props.mode === "create" ? "Create a New Case Study" : `Edit ${model.title || "Case Study"}` }}
                </UiText>
                <UiChip
                    v-if="props.mode === 'edit'"
                    :text="storyStatusChip.text"
                    :variant="storyStatusChip.variant"
                    size="sm"
                    class="shrink-0"
                />
            </div>

            <div class="flex shrink-0 flex-nowrap items-center justify-end gap-3 self-end md:self-auto">
                <template v-if="props.mode === 'create' || model.status === 'draft'">
                    <UiButton variant="outline" label="Save as Draft" :disabled="!!titleError || props.busy" @click="save('draft')" />
                    <UiButton variant="primary" label="Publish Case Study" :disabled="!!titleError || props.busy" @click="save('published')" />
                </template>
                <template v-else>
                    <UiButton variant="outline" label="Set to Draft" :disabled="props.busy" @click="save('draft')" />
                    <UiButton variant="primary" label="Save" :disabled="!!titleError || props.busy" @click="save('published')" />
                </template>
            </div>
        </div>

        <div class="flex flex-col gap-6 bg-white p-6">
            <div class="flex flex-col gap-2">
                <UiText as="label" variant="small" class="text-se-dark" text="Case Study Title" />
                <UiInputField
                    v-model="model.title"
                    placeholder="ex. How United by Blue Increased Conversion Rate by 45% with a Shopify Redesign"
                    :invalid="!!titleError"
                    :error="titleError || undefined"
                />
                <UiText as="p" variant="small" class="text-se-gray-700" text="Use a clear outcome in the title if possible." />
            </div>

            <FormGroupPanel title="Media Gallery" subtitle="Upload a video and multiple images that showcase story.">
                <div class="flex flex-col gap-4">
                    <div class="rounded-lg border border-se-gray-300 bg-white p-4">
                        <UiSingleFileUpload
                            v-model="model.thumbnail"
                            accept="image/*"
                            title="Case Study Thumbnail"
                            description="Upload the main image for this case study. It will be used as the cover image in listings and previews."
                            button-text="Upload Thumbnail"
                            note="Recommended aspect ratio: 16:9. Supported file formats: JPG, PNG, GIF, WEBP."
                            dropzone-text="Drag and drop or click to upload
your image file"
                            aspect-ratio="16:9"
                            collection="customer_story_thumbnail"
                            :upload-fn="uploadFn"
                            :delete-fn="deleteFn"
                            auto-upload
                        />
                    </div>

                    <div class="rounded-lg border border-se-gray-300 bg-white p-4">
                        <div class="mb-4 flex flex-col gap-1 md:flex-row md:items-start md:justify-between">
                            <div class="flex flex-col gap-1 max-w-1/2">
                                <UiText as="h4" variant="h6" class="font-semibold text-se-dark" text="Case Study Images" />
                                <UiText as="p" variant="small" class="text-se-gray-700" text="Upload additional images that help illustrate this case study. These can include app screenshots, results, workflows, or key highlights." />
                            </div>
                            <UiText as="p" variant="small" class="text-se-dark" text="You can upload up to 8 images" />
                        </div>

                        <UiFileUpload
                            v-model="model.gallery"
                            :max-files="8"
                            :show-thumbnails="false"
                            accept="image/*"
                            primary-text="Upload Images"
                            secondary-text="Recommended aspect ratio: 16:9. Supported file formats: JPG, PNG, GIF, WEBP."
                            aspect-ratio="16:9"
                            collection="customer_story_images"
                            :upload-fn="uploadFn"
                            :delete-fn="deleteFn"
                            auto-upload
                        />

                        <UiSortableImageList
                            v-model="model.gallery"
                            class="mt-4"
                            @delete="removeGalleryItem"
                        />
                    </div>
                </div>
            </FormGroupPanel>

            <FormGroupPanel title="Client Details">
                <div class="grid gap-6 md:grid-cols-2">
                    <div class="flex flex-col gap-1">
                        <UiText as="label" variant="small" text="Client / brand name" />
                        <UiInputField v-model="model.brand_name" placeholder="ex. United by Blue" />
                    </div>
                    <div class="flex flex-col gap-1">
                        <UiText as="label" variant="small" text="Client website" />
                        <UiInputField v-model="model.website" placeholder="ex. www.unitedbyblue.com" />
                    </div>
                    <div class="flex flex-col gap-1">
                        <UiText as="label" variant="small" text="Industry" />
                        <UiSelect v-model="model.industry" :options="industryOptions" placeholder="Select an option" />
                    </div>
                    <div class="flex flex-col gap-1">
                        <UiText as="label" variant="small" text="Shopify Plan" />
                        <UiSelect v-model="model.shopify_plan" :options="SHOPIFY_PLAN_OPTIONS" placeholder="Select an option" />
                    </div>
                </div>

                <div class="flex flex-col gap-1">
                    <UiText as="label" variant="small" text="About the client" />
                    <UiRichTextEditor v-model="model.about_the_client" placeholder="ex. United By Blue is a sustainable outdoor lifestyle brand..." :rows="4" />
                </div>

                <div class="flex flex-col gap-1">
                    <UiText as="label" variant="small" text="Target customers" />
                    <UiRichTextEditor v-model="model.target_customers" placeholder="ex. United By Blue primarily serves environmentally conscious consumers..." :rows="4" />
                </div>

                <div class="flex flex-col gap-1">
                    <UiText as="label" variant="small" text="Services provided" />
                    <UiMultiSelectChips
                        v-model="model.service_subcategory_ids"
                        :options="subcategoryOptions"
                        :option-groups="subcategoryOptionGroups"
                        placeholder="Select the service/services that you provided to the client"
                        open-on-click
                        show-chevron
                    />
                </div>

                <UiCheckbox v-model="model.hired_at_shopexperts" label="Hired on Shopexperts" />
            </FormGroupPanel>

            <FormGroupPanel title="The Challenge">
                <div class="flex flex-col gap-1">
                    <UiText as="label" variant="small" text="Project goals" />
                    <UiRichTextEditor v-model="model.project_goals" placeholder="ex. The goal of this project was to improve the ecommerce shopping experience..." :rows="4" />
                </div>

                <div class="flex flex-col gap-1">
                    <UiText as="label" variant="small" text="Problems before the project" />
                    <UiRichTextEditor v-model="model.problem_before_the_project" placeholder="ex. As the product catalog expanded, navigation became more complex..." :rows="4" />
                </div>

                <MetricsRow v-model="model.metrics" title="Baseline Metrics" subtitle="before the project" mode="before" @edit-metrics="metricsDialogOpen = true" />
            </FormGroupPanel>

            <FormGroupPanel title="Solution">
                <div class="flex flex-col gap-1">
                    <UiText as="label" variant="small" text="Project overview" />
                    <UiRichTextEditor v-model="model.project_overview" placeholder="ex. We partnered with United By Blue to improve their Shopify experience..." :rows="4" />
                </div>

                <div class="flex flex-col gap-1">
                    <UiText as="label" variant="small" text="Key improvements implemented" />
                    <UiRichTextEditor v-model="model.key_improvements_implemented" placeholder="ex. Redesigned product pages to highlight sustainability features..." :rows="4" />
                </div>

                <div class="flex flex-col gap-1">
                    <UiText as="label" variant="small" text="App and Theme Skills" />
                    <UiMultiSelectChips
                        v-model="model.badge_ids"
                        :options="badgeOptions"
                        placeholder="Select apps and theme skills used on this project"
                        open-on-click
                        show-chevron
                    />
                </div>

                <div class="flex flex-col gap-1">
                    <UiText as="label" variant="small" text="Project Duration" />
                    <UiDatePicker
                        v-model="model.date_range"
                        range
                        placeholder="Select project dates"
                        default-view="month"
                        min-view="month"
                        max-view="year"
                    />
                </div>
            </FormGroupPanel>

            <FormGroupPanel title="Results">
                <div class="flex flex-col gap-1">
                    <UiText as="label" variant="small" text="Results summary" />
                    <UiRichTextEditor v-model="model.result_summary" placeholder="ex. After launching the improved Shopify experience..." :rows="3" />
                </div>

                <MetricsRow v-model="model.metrics" title="Baseline Metrics" subtitle="after your work" mode="after" @edit-metrics="metricsDialogOpen = true" />
            </FormGroupPanel>

            <FormGroupPanel title="Client Testimonial">
                <div class="grid gap-6 md:grid-cols-2">
                    <div class="flex flex-col gap-1">
                        <UiText as="label" variant="small" text="Client name" />
                        <UiInputField v-model="model.client_name" placeholder="ex. Brian Linton" />
                    </div>
                    <div class="flex flex-col gap-1">
                        <UiText as="label" variant="small" text="Role" />
                        <UiSelect v-model="model.client_role" :options="TESTIMONIAL_ROLE_OPTIONS" placeholder="Select an option" />
                    </div>
                </div>

                <div class="flex flex-col gap-1">
                    <UiText as="label" variant="small" text="Client testimonial" />
                    <UiTextarea v-model="model.client_testimonial" placeholder='ex. “Working with this expert helped us improve the performance of our Shopify store...”' :rows="4" />
                </div>
            </FormGroupPanel>
        </div>

        <MetricsDialog v-model:open="metricsDialogOpen" v-model="model.metrics" />
    </div>
</template>
